// Pull a turn-indexed view out of a battle_N.jsonl frame stream.
//
// The orchestrator writes frames in this order:
//
//   1. open `meta`   — seed, agent identities, loadouts
//   2. per turn t in 0..N:
//        `state`   (frame: "state", turn: t) — snapshot before actions
//        `actions` (frame: "actions", turn: t) — what A/B did this turn
//   3. close `state` — final state after the last actions
//   4. close `meta`  — ended/final_turn/reason/winner
//
// The viewer wants O(1) lookup by turn index, so we collapse the
// stream into:
//   - openMeta / closeMeta
//   - states[]: snapshots, one per turn (including the post-final state)
//   - turns[]:  one per action frame, paired with the next state
//
// Frames the viewer doesn't recognize are ignored (forward compat).

import type {
  ActionsFrame,
  BattleFrame,
  BattleState,
  MetaFrame,
  StateFrame,
  OtherFrame,
} from "./types";

export interface TurnRecord {
  turn: number;
  preState: BattleState;
  postState: BattleState | null; // null only if the stream was truncated mid-turn
  action_a?: unknown;
  action_b?: unknown;
  events: unknown[];
}

export interface ParsedBattle {
  openMeta: MetaFrame | null;
  closeMeta: OtherFrame | null;
  states: BattleState[];
  turns: TurnRecord[];
}

function isActionsFrame(f: BattleFrame): f is BattleFrame & ActionsFrame {
  return f.frame === "actions";
}

export function parseBattle(frames: BattleFrame[]): ParsedBattle {
  let openMeta: MetaFrame | null = null;
  let closeMeta: OtherFrame | null = null;
  const states: BattleState[] = [];
  const stateTurns: number[] = []; // turn label per state, parallel array
  const actions: ActionsFrame[] = [];

  for (const f of frames) {
    if (f.frame === "meta") {
      const meta = f as unknown as MetaFrame & { ended?: boolean };
      if (meta.ended === true) {
        closeMeta = f as OtherFrame;
      } else if (openMeta === null) {
        openMeta = meta;
      } else {
        // Mid-stream meta we don't recognize — keep last so close wins.
        closeMeta = f as OtherFrame;
      }
    } else if (f.frame === "state") {
      const s = f as StateFrame & { turn?: number };
      states.push(s.state);
      stateTurns.push(typeof s.turn === "number" ? s.turn : states.length - 1);
    } else if (isActionsFrame(f)) {
      actions.push(f as ActionsFrame);
    }
    // Unknown frames are ignored.
  }

  // Pair each action frame with the state immediately following it.
  // A well-formed stream emits state(i) before actions(i), so the
  // index-based join holds. A malformed stream (more `actions` than
  // `states`, or `actions` before any `state`) would leave
  // `preState` undefined; drop those turns rather than emit a
  // half-shaped record that BattleView would then have to defend
  // against.
  const turns: TurnRecord[] = [];
  for (let i = 0; i < actions.length; i++) {
    const pre = states[i];
    if (pre === undefined) continue;
    const a = actions[i];
    turns.push({
      turn: a.turn,
      preState: pre,
      postState: states[i + 1] ?? null,
      action_a: a.action_a,
      action_b: a.action_b,
      events: Array.isArray(a.events) ? a.events : [],
    });
  }

  return { openMeta, closeMeta, states, turns };
}

// Convenience: total turns played (== actions.length). Useful as the
// scrubber upper bound.
export function totalTurns(parsed: ParsedBattle): number {
  return parsed.turns.length;
}
